/* eslint-disable react/display-name */
import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { dataLayer } from '@weatherbug/common';
import { reduxConnect } from '../../common';
import { DewpointAndMonthlyRainView } from './obsDetailsComponents/dewpointAndMonthlyRainView';
import { HumidityAndAverageWindView } from './obsDetailsComponents/humidityAndAverageWindView';
import { PressureAndWindGustView } from './obsDetailsComponents/pressureAndWindGustView';
import { WindChillAndDailyRainView } from './obsDetailsComponents/windChillAndDailyRainView';
import { useInterval } from '../../common/hooks/useInterval';
import type { IStoreState, IStdParameters } from '../common/interfaces/types';

const locationType = 'latitudelongitude';
const verbose = false;
const refresh = false;
const offset = 0;
const defaultObsInterval = 5 * 60 * 1000;
const roundingProfile = 'displayV1';
const includePrecip = true;

type ObservationDetailsProps = {
    latitude: number;
    longitude: number;
    authId: string;
    secret: string;
    obsBaseUrl: string;
    tempUnit: number;
    windUnit: number;
    pressureUnit: number;
    precipitationUnit: number;
    stdParameters: IStdParameters;
    isActivePageTab: boolean;
    stationId: string;
    providerId: string | number;
    getStationObservations: typeof dataLayer.actions.getStationObservations;
};

const mapStoreStateToProps = (storeState: IStoreState) => {
    const { pageVisibility, units } = storeState || {};
    const appState = storeState.appState.appState || {};
    const { authId, secret, obsBaseUrl, stdParameters, user } = appState;
    const { ActiveLocation } = user || {};
    const { Latitude: latitude, Longitude: longitude } = ActiveLocation?.Locality || {};
    const { StationId: stationId, ProviderId: providerId } = ActiveLocation?.Station || {};
    const { windUnit, pressureUnit, tempUnit } = units || {};
    const isActivePageTab = pageVisibility?.visible;

    return {
        authId,
        secret,
        obsBaseUrl,
        tempUnit,
        windUnit,
        pressureUnit,
        latitude: latitude,
        longitude: longitude,
        precipitationUnit: tempUnit,
        stdParameters,
        isActivePageTab,
        stationId,
        providerId
    };
};

const mapDispatchToProps = {
    getStationObservations: dataLayer?.actions?.getStationObservations,
    getPrecipitationMonthlyByLatLon: dataLayer?.actions?.getPrecipitationMonthlyByLatLon
};

export const ObservationDetails = reduxConnect(mapStoreStateToProps, mapDispatchToProps)(memo(({
    latitude,
    longitude,
    authId,
    secret,
    obsBaseUrl,
    tempUnit,
    windUnit,
    pressureUnit,
    precipitationUnit,
    stdParameters,
    isActivePageTab,
    stationId,
    providerId,
    getStationObservations
}: ObservationDetailsProps) => {
    useInterval(
        () => {
            if (obsBaseUrl && authId && secret && Number.isFinite(latitude) && Number.isFinite(longitude)) {
                getStationObservations({ latitude, longitude, temperatureUnit: tempUnit, speedUnit: windUnit, pressureUnit, includePrecip, precipitationUnit, precipitationRateUnit: precipitationUnit, stdParameters, verbose, refresh, authId, secret, baseUrl: obsBaseUrl, offset, locationType, roundingProfile, stationId, providerId });
            }
        },
        defaultObsInterval,
        true,
        !!(isActivePageTab && obsBaseUrl && authId && secret && latitude && longitude && stdParameters && precipitationUnit && tempUnit && pressureUnit && windUnit && getStationObservations),
        [latitude, longitude, precipitationUnit, tempUnit, pressureUnit, windUnit, authId, secret, obsBaseUrl, isActivePageTab]
    );

    return (
        <>
            <WindChillAndDailyRainView />
            <DewpointAndMonthlyRainView />
            <HumidityAndAverageWindView />
            <PressureAndWindGustView />
        </>
    );
}));

ObservationDetails.displayName = 'ObservationDetails';
ObservationDetails.propTypes = {
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    authId: PropTypes.string,
    secret: PropTypes.string,
    obsBaseUrl: PropTypes.string,
    getCurrentStationObsByLatLon: PropTypes.func,
    tempUnit: PropTypes.number,
    precipitationUnit: PropTypes.number,
    pressureUnit: PropTypes.number,
    windUnit: PropTypes.number,
    stdParameters: PropTypes.object
};

